import {
  OPS_AUTH,
  casePaymentPlanPath,
  casePreApprovalPath,
  paymentPlanUpdatePath,
  paymentStatusPath,
  preApprovalStatusPath,
  preApprovalUpdatePath,
} from "../config/api";
import { apiRequest, unwrapApiEnvelope } from "./apiClient";

function toQuery(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

const LIST_PAGE_LIMIT = 100;
const LIST_TTL_MS = 60_000;
const paymentListCache = new Map();

function runPaymentListDeduped(key, factory, { force = false } = {}) {
  const hit = paymentListCache.get(key);
  if (!force && hit?.result != null && Date.now() - hit.at < LIST_TTL_MS) {
    return Promise.resolve(hit.result);
  }
  if (!force && hit?.promise) return hit.promise;

  const promise = Promise.resolve()
    .then(factory)
    .then((result) => {
      paymentListCache.set(key, { at: Date.now(), result, promise: null });
      return result;
    })
    .catch((err) => {
      const current = paymentListCache.get(key);
      if (current?.promise === promise) paymentListCache.delete(key);
      throw err;
    });

  paymentListCache.set(key, { at: hit?.at || 0, result: hit?.result ?? null, promise });
  return promise;
}

async function fetchAllPages(token, path, extract, extra = {}) {
  const rows = [];
  let page = 1;
  let lastPage = 1;
  do {
    const json = await apiRequest(`${path}${toQuery({ page, limit: LIST_PAGE_LIMIT, ...extra })}`, {
      method: "GET",
      token,
    });
    const data = unwrapApiEnvelope(json);
    rows.push(...(extract(data) || []));
    lastPage = Number(data?.pagination?.last_page || 1);
    page += 1;
  } while (page <= lastPage && page <= 40);
  return rows;
}

export async function fetchPaymentGateway(token) {
  const json = await apiRequest(OPS_AUTH.paymentGateway, { method: "GET", token });
  return unwrapApiEnvelope(json)?.gateway || unwrapApiEnvelope(json);
}

export async function updatePaymentGateway(token, payload) {
  const json = await apiRequest(OPS_AUTH.paymentGateway, {
    method: "PUT",
    token,
    body: payload,
  });
  return unwrapApiEnvelope(json);
}

export async function refreshPaymentGatewayToken(token) {
  const json = await apiRequest(OPS_AUTH.paymentGatewayToken, { method: "POST", token });
  return unwrapApiEnvelope(json);
}

export async function verifyOpsPaymentStatus(token, paymentId) {
  const json = await apiRequest(paymentStatusPath(paymentId), { method: "GET", token });
  return unwrapApiEnvelope(json);
}

export async function fetchOpsPreApprovals(token, extra = {}, { force = false } = {}) {
  return runPaymentListDeduped(
    `preapprovals:${token}:${JSON.stringify(extra)}`,
    () => fetchAllPages(token, OPS_AUTH.preApprovals, (data) => data?.mandates || [], extra),
    { force }
  );
}

export async function fetchPreApprovalHistory(token, msisdn) {
  const json = await apiRequest(`${OPS_AUTH.preApprovalHistory}${toQuery({ msisdn })}`, {
    method: "GET",
    token,
  });
  return unwrapApiEnvelope(json);
}

export async function requestCasePreApproval(token, caseId, payload) {
  const json = await apiRequest(casePreApprovalPath(caseId), {
    method: "POST",
    token,
    body: payload,
  });
  return unwrapApiEnvelope(json);
}

export async function updateOpsPreApproval(token, referenceId, payload) {
  const json = await apiRequest(preApprovalUpdatePath(referenceId), {
    method: "PATCH",
    token,
    body: payload,
  });
  return unwrapApiEnvelope(json);
}

export async function checkPreApprovalStatus(token, referenceId) {
  const json = await apiRequest(preApprovalStatusPath(referenceId), { method: "GET", token });
  return unwrapApiEnvelope(json);
}

export async function fetchOpsPaymentPlans(token, extra = {}, { force = false } = {}) {
  return runPaymentListDeduped(
    `plans:${token}:${JSON.stringify(extra)}`,
    () => fetchAllPages(token, OPS_AUTH.paymentPlans, (data) => data?.plans || [], extra),
    { force }
  );
}

export async function createCasePaymentPlan(token, caseId, payload) {
  const json = await apiRequest(casePaymentPlanPath(caseId), {
    method: "POST",
    token,
    body: payload,
  });
  return unwrapApiEnvelope(json);
}

export async function updateOpsPaymentPlan(token, planId, payload) {
  const json = await apiRequest(paymentPlanUpdatePath(planId), {
    method: "PATCH",
    token,
    body: payload,
  });
  return unwrapApiEnvelope(json);
}
